import './index.css'
import { Theme } from '@radix-ui/themes'
import * as React from 'react'
import { MdPhotoSizeSelectLarge } from '@react-icons/all-files/md/MdPhotoSizeSelectLarge'
import { connect } from 'react-redux'
import type { PlaylistTheme, PlaylistThemeUpdates } from 'types/playlist'
import {
  createTheme,
  deleteTheme,
  updateTheme,
} from '@disco/state-management/reducers/themes'
import FileSelect from 'components/common/file-select'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import ErrorText from 'components/ui/error-text'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import FileDropWrapper from 'containers/FileDropWrapper'
import type { RootState } from 'store/configureStore'
const MAX_SIZE = 2000000
type Props = {
  theme: PlaylistTheme | null | undefined
  onSave?: (arg0: PlaylistTheme) => void
  createTheme: typeof createTheme
  updateTheme: typeof updateTheme
  deleteTheme: typeof deleteTheme
  closeModal: () => void
  themesError: Record<string, any> | null | undefined
}
type State = {
  theme: PlaylistTheme | null | undefined
  name: string
  defaultTheme: boolean
  logo: string | null | undefined
  logoFile: File | null | undefined
  logoUploadProgress: number | null | undefined
  // in percents
  image: string | null | undefined
  imageFile: File | null | undefined
  imageUploadProgress: number | null | undefined
  // in percents
  error: string | null | undefined
  sizeError: string | null | undefined
}

class EditTheme extends React.Component<Props, State> {
  state = {
    theme: this.props.theme || null,
    name: this.props.theme?.name || '',
    defaultTheme: this.props.theme?.default || false,
    logo: this.props.theme?.logo || undefined,
    logoFile: undefined,
    logoUploadProgress: null,
    image: this.props.theme?.image || undefined,
    imageFile: undefined,
    imageUploadProgress: null,
    error: undefined,
    sizeError: null,
  }
  _onDefaultThemeSwitch = () => {
    this.setState(prevState => ({
      defaultTheme: !prevState.defaultTheme,
    }))
  }
  _processImageFiles = (field: string, files: Array<File>) => {
    const file = files[0]
    if (!file) return

    if (field === 'logo') {
      this.setState({
        logoFile: file,
      })
    } else if (field === 'image') {
      this.setState({
        imageFile: file,
      })
    }

    // show preview without triggering server upload
    const reader = new FileReader()

    reader.onload = (e: any) => {
      if (field === 'logo') {
        this.setState({
          logo: e.target.result,
        })
      } else if (field === 'image') {
        this.setState({
          image: e.target.result,
        })
      }
    }

    reader.readAsDataURL(file)
  }
  _onFileDrop = (field: string, files: Array<File>) => {
    const file = files[0]
    // handle images only
    if (!file || file.type.indexOf('image/') !== 0) return

    if (file.size <= MAX_SIZE) {
      if (this.state.sizeError) {
        this.setState({
          sizeError: null,
        })
      }

      this._processImageFiles(field, files)
    } else {
      this.setState({
        sizeError:
          'Sorry! That image is too big. Please upload an image under 2MB.',
      })
    }
  }
  _handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      name: evt.target.value,
    })
  }
  _handleDelete = (evt: React.SyntheticEvent) => {
    evt.preventDefault()
    if (this.state.theme) this.props.deleteTheme(this.state.theme)
    this.props.closeModal()
  }
  // updates image upload progress bars
  _updateProgress = ({
    themeUpdates,
    progress,
  }: {
    themeUpdates: PlaylistThemeUpdates
    progress: number
  }) => {
    if (themeUpdates.logo) {
      this.setState({
        logoUploadProgress: progress,
      })
    }

    if (themeUpdates.image) {
      this.setState({
        imageUploadProgress: progress,
      })
    }
  }

  _sendUpdateData({
    themeUpdates,
    theme,
  }: {
    themeUpdates: PlaylistThemeUpdates
    theme: PlaylistTheme | null | undefined
  }) {
    if (theme) {
      return this.props.updateTheme({
        savedTheme: theme,
        theme: themeUpdates,
        onProgress: progress => {
          this._updateProgress({
            themeUpdates,
            progress,
          })
        },
      })
    } else {
      // create theme
      return this.props.createTheme({
        theme: themeUpdates,
        onProgress: progress => {
          this._updateProgress({
            themeUpdates,
            progress,
          })
        },
      })
    }
  }

  _handleSubmit = async (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()

    if (this.state.name.length === 0) {
      this.setState({
        error: 'Please give your theme a name before saving',
      })
      return
    }

    // for images undefined means that we do not change anything and leave
    // theme images as it is, if image set to null, then it means that we
    // delete image from theme
    let logo
    let image

    if (this.state.logoFile) {
      logo = this.state.logoFile
    } else if (this.state.logo === null) {
      logo = null
    }

    if (this.state.imageFile) {
      image = this.state.imageFile
    } else if (this.state.imageFile === null) {
      image = null
    }

    const themeUpdates = {
      name: this.state.name,
      logo: logo,
      image: image,
      defaultTheme: this.state.defaultTheme,
    }

    if (this.state.sizeError) {
      this.setState({
        sizeError: null,
      })
    }

    if (themeUpdates.logo && themeUpdates.image) {
      const themeUpdatesWithoutLogo = { ...themeUpdates }
      delete themeUpdatesWithoutLogo.logo
      let savedTheme
      const { payload } = await this._sendUpdateData({
        themeUpdates: themeUpdatesWithoutLogo,
        theme: this.state.theme,
      })
      savedTheme = payload
      const themeUpdatesWithoutImage = { ...themeUpdates }
      delete themeUpdatesWithoutImage.image
      const { payload: updatedTheme } = await this._sendUpdateData({
        themeUpdates: themeUpdatesWithoutImage,
        theme: savedTheme,
      })
      savedTheme = updatedTheme
      if (this.props.onSave) this.props.onSave(savedTheme)
      this.props.closeModal()
    } else {
      const { payload: savedTheme } = await this._sendUpdateData({
        themeUpdates,
        theme: this.state.theme,
      })
      if (this.props.onSave) this.props.onSave(savedTheme)
      this.props.closeModal()
    }
  }

  render() {
    const { closeModal, themesError } = this.props
    const { defaultTheme, error, name, theme, sizeError } = this.state
    const header = theme ? 'Theme options' : 'Create a theme'
    const buttonText = theme ? 'Update Theme' : 'Create Theme'
    return (
      <Theme>
        <div className="modal-width--600">
          <Heading>{header}</Heading>
          <Row columnsSize={[12]} marginBottom="small">
            <div>
              <TextField
                label="Name"
                value={name}
                onChange={this._handleNameChange}
                error={error}
              />
            </div>
          </Row>
          {this._renderImageFields()}
          {sizeError && <ErrorText>{sizeError}</ErrorText>}
          {themesError && <ErrorText>{themesError?.message}</ErrorText>}
          <Checkbox
            label="Set as default"
            checked={defaultTheme}
            onChange={this._onDefaultThemeSwitch}
          />
          <div className="Form_ButtonsContainer separated-small">
            <ButtonContainer>
              <Button caption="Cancel" onClick={closeModal} />
              <Button
                appearance="primary"
                caption={buttonText}
                onClick={this._handleSubmit}
              />
              {this._renderDelete()}
            </ButtonContainer>
          </div>
        </div>
      </Theme>
    )
  }

  _renderImageFields() {
    return (
      <Row columnsSize={[8, 4]} marginBottom="small">
        <div>
          {this._renderImageField(
            'Background Image',
            'image',
            'cover',
            'We recommend .jpg files at 1200 × 675 px. Maximum file size: 2MB',
          )}
        </div>
        <div>
          {this._renderImageField(
            'Logo',
            'logo',
            'contain',
            '.png file at 200 × 200 px',
          )}
        </div>
      </Row>
    )
  }

  _renderImageField(
    label: string,
    field: string,
    backgroundSize: string,
    dimensions: string,
  ) {
    return (
      <React.Fragment>
        <Label caption={label} />
        <FileDropWrapper onDrop={files => this._onFileDrop(field, files)}>
          <FileSelect
            className="Form_Droparea"
            onChange={files => this._onFileDrop(field, files)}
          >
            {this._renderBackground(field, backgroundSize)}
          </FileSelect>
        </FileDropWrapper>
        <div className="Form_Hint">{dimensions}</div>
      </React.Fragment>
    )
  }

  _removeImage(e: React.SyntheticEvent<any>, field: string) {
    e.preventDefault()
    e.stopPropagation()
    if (field === 'image')
      this.setState({
        image: null,
        imageFile: null,
      })
    if (field === 'logo')
      this.setState({
        logo: null,
        logoFile: null,
      })
  }

  _renderBackground(field: string, backgroundSize: string) {
    if (this.state[field]) {
      const backgroundStyle = {
        backgroundImage: `url("${this.state[field]}")`,
        backgroundSize: backgroundSize,
        backgroundRepeat: 'no-repeat',
      }
      let progressBar: React.ReactNode = null

      if (this.state[`${field}UploadProgress`] && !this.props.themesError) {
        const progress = 100 - this.state[`${field}UploadProgress`]
        const progressStyle = {
          MozTransform: `translateX(-${progress}%)`,
          MsTransform: `translateX(-${progress}%)`,
          WebkitTransform: `translateX(-${progress}%)`,
          transform: `translateX(-${progress}%)`,
        }
        progressBar = (
          <div className="Form_Droparea_Progress">
            <div style={progressStyle} className="Form_Droparea_ProgressBar" />
          </div>
        )
      }

      let removeButton: React.ReactNode = null

      if (!progressBar) {
        removeButton = (
          <button
            className="Form_ImageRemove Form_ImageRemove--EditTheme"
            onClick={e => this._removeImage(e, field)}
          >
            <i className="RemoveIcon" />
          </button>
        )
      }

      return (
        <div className="Form_Droparea_Background" style={backgroundStyle}>
          {removeButton}
          {progressBar}
        </div>
      )
    }

    return (
      <React.Fragment>
        <div className="Form_Droparea_PlaceholderImg">
          <MdPhotoSizeSelectLarge />
        </div>
        <div className="Form_Droparea_PlaceholderText">
          Drag files here, or click to browse
        </div>
      </React.Fragment>
    )
  }

  _renderDelete() {
    if (!this.state.theme) return null
    return (
      <Button
        appearance="danger"
        caption="Delete Theme"
        onClick={this._handleDelete}
      />
    )
  }
}

export default connect(
  (state: RootState) => ({
    themesError: state.themes.error,
  }),
  {
    createTheme,
    updateTheme,
    deleteTheme,
  },
)(EditTheme)
